import { useCallback, useMemo } from "react";
import getCenterAddress from "../../../common/getCenterAddress";
import { useAppointmentBookingContext } from "../../Appointment/AppointmentBookingContext";
import { BookingCenter } from "../../Appointment/AppointmentBookingContext/useBookingCenters";
import { CenterPreviewProps } from "../CenterPreview";
import useTranslations from "./useTranslations";
import { formatInternational } from "../../../common/phoneNumberHelpers";

const centerToCenterPreviewProps = (
  center: BookingCenter,
): CenterPreviewProps & { id: string } => {
  const {
    country,
    name,
    addresses,
    city,
    postalCode,
    phoneNumber,
    email,
    state,
    faxNumber,
    operatingHours,
  } = center;

  const description = getCenterAddress({
    country,
    addresses,
    city,
    postalCode,
    state,
  });

  const formattedPhoneNumber = phoneNumber
    ? formatInternational({ phoneNumber })
    : "";

  const formattedFaxNumber = faxNumber
    ? formatInternational({ phoneNumber: faxNumber })
    : "";

  const phone = {
    styled: formattedPhoneNumber,
    link: `tel:${phoneNumber}`,
  };

  const fax =
    faxNumber && formattedFaxNumber
      ? {
          styled: formattedFaxNumber,
          link: `tel:${faxNumber}`,
        }
      : undefined;

  return {
    id: center.id,
    name,
    description,
    phone,
    email,
    fax,
    operatingHours,
  };
};

const useCenterPreviewPropsList = () => {
  const { btnBook, btnChange } = useTranslations();

  const { centers, selectedCenter, onCenterChange, isSubmitLoading } =
    useAppointmentBookingContext();

  const { data, status } = centers;

  const onPreviewActionClickFunc = useCallback(
    (id: string) => {
      return () => {
        if (status !== "success" || selectedCenter) {
          onCenterChange(null);
          return;
        }

        const foundCenter = data.find((center) => center.id === id);

        onCenterChange(foundCenter || null);
      };
    },
    [data, onCenterChange, selectedCenter, status],
  );

  const centerPreviewPropsList = useMemo<
    (CenterPreviewProps & { id: string })[]
  >(() => {
    if (status !== "success") return [];

    if (selectedCenter) {
      const selectedCenterPreviewProps =
        centerToCenterPreviewProps(selectedCenter);

      const isActionButtonShown = data.length !== 1;

      if (isActionButtonShown) {
        selectedCenterPreviewProps.actionButton = {
          props: {
            variant: "outlined",
            disabled: isSubmitLoading,
            onClick: onPreviewActionClickFunc(selectedCenter.id),
          },
          text: btnChange,
        };
      }

      return [selectedCenterPreviewProps];
    }

    const centerPreviewPropsOptions = data.map((center) => {
      const centerPreviewProps = centerToCenterPreviewProps(center);

      centerPreviewProps.actionButton = {
        props: {
          size: "large",
          variant: "contained",
          disabled: isSubmitLoading,
          onClick: onPreviewActionClickFunc(center.id),
        },
        text: btnBook,
      };

      return centerPreviewProps;
    });

    return centerPreviewPropsOptions;
  }, [
    btnBook,
    btnChange,
    data,
    isSubmitLoading,
    onPreviewActionClickFunc,
    selectedCenter,
    status,
  ]);

  return { centerPreviewPropsList };
};

export default useCenterPreviewPropsList;
